import useAlertMessage from "hooks/useAlertMessage";
import useLoading from "hooks/useLoading";
import {useState} from "react";
import {useFormContext} from "react-hook-form";
import {IdAndName} from "types/GenericTypes";
import {getUpdatedFields} from "util/getDirtyFieldsFromRHF";
import {AddOrEditVoyageData} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/Voyages/AddOrEditVoyageModal/AddOrEditVoyageModal";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {
  Voyage,
  VoyageToSend,
} from "views/ProcessDetails/refactor/types/voyages.types";
import {
  calculateInitialValues,
  mapVoyageDataToSend,
} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/helpers/voyageModalHelpers";
import {buildVoyagesDraftAfterInsert} from "views/ProcessDetails/refactor/components/ProcessBoarding/components/VoyagesV2/helpers/draftVoyageFormHelpers";
import {
  ID_DELIVERY_VOYAGE,
  ID_FINAL_DESTINATION_VOYAGE,
  ID_TRANSHIPMENT_TYPE_VOYAGE,
} from "views/ProcessDetails/utils/constants";

type Props = {
  addNewVoyage: (
    id: number,
    data: Partial<VoyageToSend> | Partial<VoyageToSend>[],
  ) => Promise<Process | undefined>;
  refetchProcess: () => Promise<Process | undefined>;
  processId: number;
  allVoyages: Voyage[];
  persistOnSubmit: boolean;
};

export const useVoyageModal = (props: Props) => {
  const {addNewVoyage, refetchProcess, processId, allVoyages, persistOnSubmit} =
    props;
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<AddOrEditVoyageData>(
    {} as AddOrEditVoyageData,
  );
  const [newVoyage, setNewVoyage] = useState<"" | "newVoyage">("");
  const [initialOrigin, setInitialOrigin] = useState<IdAndName | undefined>();
  const [initialDestination, setInitialDestination] = useState<
    IdAndName | undefined
  >();
  const [setLoading] = useLoading();
  const [showAlert] = useAlertMessage();
  const {getValues, reset, resetField, setValue} = useFormContext<Process>();

  const handleSubmitVoyage = async (
    receivedData: AddOrEditVoyageData,
    dirty: any,
  ) => {
    if (!persistOnSubmit) {
      const updatedVoyages = buildVoyagesDraftAfterInsert(
        allVoyages,
        receivedData,
      );

      setValue("boarding.voyages", updatedVoyages, {shouldDirty: true});
      closeModal();
      return;
    }
    const dataToSend = getUpdatedFields(dirty, receivedData);
    const firstDtaOrDeliveryVoyageFromProcess = allVoyages?.find(
      (voyage) =>
        voyage?.typeVoyage?.id === ID_FINAL_DESTINATION_VOYAGE ||
        voyage.typeVoyage?.id === ID_DELIVERY_VOYAGE,
    );

    const shouldUpdateNextVoyageOrigin = () => {
      const hasDtaOrDeliveryVoyage = !!firstDtaOrDeliveryVoyageFromProcess?.id;

      if (
        dataToSend?.typeVoyage?.id === ID_TRANSHIPMENT_TYPE_VOYAGE &&
        hasDtaOrDeliveryVoyage
      ) {
        return true;
      }
      return false;
    };

    const dtaOrDeliveryVoyageToEditOrigin: Partial<VoyageToSend> = {
      id: firstDtaOrDeliveryVoyageFromProcess?.id,
      idOrigin: dataToSend?.destination?.id,
    };

    const data = mapVoyageDataToSend(dataToSend, receivedData);

    try {
      setLoading(true);
      await addNewVoyage(
        processId,
        shouldUpdateNextVoyageOrigin()
          ? [data, dtaOrDeliveryVoyageToEditOrigin]
          : data,
      );
      await refetchProcessAndResetForm();
      closeModal();
    } catch (error: any) {
      showAlert(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const refetchProcessAndResetForm = async () => {
    const response = await refetchProcess();
    reset(response);
    resetField("boarding.voyages");
  };

  const openModal = (index: number | null, voyages: Voyage[]) => {
    setIsOpen(true);
    if (index && index > 0) {
      const currentVoyages = getValues("boarding.voyages") as Voyage[];
      const {origin, destination} = calculateInitialValues(
        currentVoyages?.length ? currentVoyages : voyages,
      );
      setInitialOrigin(origin ?? undefined);
      setInitialDestination(destination ?? undefined);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalData({});
    setInitialOrigin(undefined);
    setInitialDestination(undefined);
    setNewVoyage("");
  };

  return {
    isOpen,
    modalData,
    newVoyage,
    initialOrigin,
    initialDestination,
    openModal,
    closeModal,
    handleSubmitVoyage,
    setNewVoyage,
  };
};
